import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  api,
  setAccessToken,
  setRefreshToken,
  getRefreshToken,
  setUnauthenticatedHandler,
  ApiError,
} from './api';
import type { Permission } from './permissions';

/**
 * Who is signed in, what they may do, and which branch they are working in.
 *
 * The permission list comes from the server on every session load (AUTH-003:
 * "every request reloads current profile status, role, tenant and branch"), so
 * a role change or suspension takes effect on the next navigation rather than
 * at the next login.
 */

export type TenantRole =
  | 'OWNER'
  | 'ADMIN'
  | 'DOCTOR'
  | 'RECEPTION'
  | 'ACCOUNTING'
  | 'LAB'
  | 'RADIOLOGY'
  | 'PHARMACY'
  | 'MEMBER';

export interface BranchSummary {
  id: string;
  nameAr: string;
  nameEn: string | null;
  timezone: string;
}

export interface SubscriptionSummary {
  status: 'TRIAL' | 'ACTIVE' | 'GRACE' | 'SUSPENDED' | 'CANCELLED' | 'EXPIRED';
  planCode: string;
  planNameAr: string;
  trialEndsAt: string | null;
  /** Modules the plan unlocks; the UI hides entries the tenant has not bought. */
  modules: string[];
  /** True when clinical writes are blocked by subscription policy (SUB-003). */
  readOnly: boolean;
}

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  role: TenantRole;
  isPlatformAdmin: boolean;
  permissions: Permission[];
  tenantId: string;
  tenantName: string;
  /** Empty means "not branch-restricted" (RBAC-006). */
  branches: BranchSummary[];
  doctorId: string | null;
  preferredLocale: 'ar' | 'en';
  mfaEnabled: boolean;
}

export interface ImpersonationContext {
  tenantName: string;
  purpose: string;
  expiresAt: string;
}

interface SessionValue {
  user: SessionUser | null;
  subscription: SubscriptionSummary | null;
  impersonation: ImpersonationContext | null;
  activeBranchId: string | null;
  loading: boolean;
  /**
   * Set when the server refused the session for a reason that signing in again
   * cannot fix - a 403 from /auth/me with no platform session behind it. The
   * auth gate reads it so such a user sees an explanation instead of being
   * redirected to the login form they just used successfully.
   */
  sessionError: 'forbidden' | null;
  setActiveBranch: (branchId: string) => void;
  /** UI capability check. Never a substitute for the server's own (RBAC-004). */
  can: (permission: Permission) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

const ACTIVE_BRANCH_KEY = 'medos.activeBranch';

interface MeResponse {
  user: SessionUser;
  subscription: SubscriptionSummary | null;
  impersonation: ImpersonationContext | null;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationContext | null>(null);
  const [activeBranchId, setActiveBranchId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /** Set when the server refused the session for a reason signing in again cannot fix. */
  const [sessionError, setSessionError] = useState<'forbidden' | null>(null);

  const load = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/auth/me');
      setSessionError(null);
      setUser(me.user);
      setSubscription(me.subscription);
      setImpersonation(me.impersonation);

      // A remembered branch is a preference, never authority: if the user's
      // grants no longer include it, fall back rather than leaving them
      // pointed at a branch the server will reject on every request.
      const remembered = localStorage.getItem(ACTIVE_BRANCH_KEY);
      const permitted = me.user.branches;
      const valid = remembered && permitted.some((b) => b.id === remembered) ? remembered : null;
      setActiveBranchId(valid ?? permitted[0]?.id ?? null);
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        setUser(null);
      } else if (error instanceof ApiError && error.status === 403) {
        // A 403 from /auth/me means the token is good but there is no tenant
        // context. For a platform operator with no clinic membership that is
        // the normal state, not an error: `TenantGuard` answers "Tenant context
        // is required" and there is nothing they can do about it.
        //
        // Before this existed, such a user logged in successfully and was sent
        // straight back to the login screen - `user` stayed null, `loading`
        // went false, and RequireAuth redirected. Silently, with nothing shown
        // and nothing logged.
        try {
          const platform = await api.get<MeResponse>('/auth/platform-session');
          setSessionError(null);
          setUser(platform.user);
          setSubscription(null);
          setImpersonation(null);
          setActiveBranchId(null);
        } catch {
          // Not an operator either. Now the 403 is real, and it must not become
          // a redirect loop: surface it so the login screen can say why rather
          // than bouncing the user back to a form that just succeeded.
          setUser(null);
          setSessionError('forbidden');
        }
      } else if (!(error instanceof ApiError)) {
        // A transient failure must not look like a sign-out. Leave the session
        // as-is; the screen's own error state reports the problem.
        throw error;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setAccessToken(null);
      // The api layer only reaches this after a refresh attempt already failed,
      // so the stored token is known-dead. Keeping it would make every
      // subsequent request pay for a refresh that cannot succeed.
      setRefreshToken(null);
      setUser(null);
    });
    void load();
  }, [load]);

  const setActiveBranch = useCallback((branchId: string) => {
    localStorage.setItem(ACTIVE_BRANCH_KEY, branchId);
    setActiveBranchId(branchId);
  }, []);

  const signOut = useCallback(async () => {
    try {
      // Send the refresh token so the server revokes this session specifically.
      await api.post('/auth/logout', { refreshToken: getRefreshToken() ?? undefined });
    } finally {
      setAccessToken(null);
      // Must be cleared too. Leaving it behind meant a sign-out followed by a
      // page reload found a live credential: /auth/me 401s, the api layer's
      // one-shot retry refreshes with it, and the user is silently signed back
      // in - including from the idle-timeout modal's sign-out.
      setRefreshToken(null);
      localStorage.removeItem(ACTIVE_BRANCH_KEY);
      setUser(null);
      setSubscription(null);
      setImpersonation(null);
    }
  }, []);

  const permissionSet = useMemo(() => new Set(user?.permissions ?? []), [user]);

  const value = useMemo<SessionValue>(
    () => ({
      user,
      subscription,
      impersonation,
      activeBranchId,
      loading,
      sessionError,
      setActiveBranch,
      can: (permission) => permissionSet.has(permission),
      canAny: (permissions) => permissions.some((p) => permissionSet.has(p)),
      signOut,
      refresh: load,
    }),
    [
      user,
      subscription,
      impersonation,
      activeBranchId,
      loading,
      sessionError,
      permissionSet,
      setActiveBranch,
      signOut,
      load,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** Convenience for the common `can(...)` call in a render. */
export function useCan(): (permission: Permission) => boolean {
  return useSession().can;
}
